// @flow
import deepmerge from "deepmerge";

import { HTTP_METHODS, TIMEVERSION_FILTER_NAME } from "../constants/Constants";

import { HIDE_WHEN_EMPTY } from "../constants/LayoutHints";

import { getBasePathModularUI, getSetting } from "../constants/Settings";

import Href from "../models/href/Href";

import universalFetch from "../utils/fetch/universalFetch";
import resolveModel from "../models/resolveModel";
import FormModel from "../models/form/FormModel";
import ContentModel from "../models/content/ContentModel";
import ErrorResponse from "../models/error/ErrorResponse";

import ModularUIResponse from "./ModularUIResponse";

import ModularUIError from "./ModularUIError";
import { IllegalArgumentException, IllegalStateException } from "../exceptions";

import { isPlainObject } from "../utils/helpers/objects";

import type { TargetModel } from "./types";
import type {
  RequestBaseOptions,
  RequestModularUIOptions,
} from "../utils/fetch/types";
import type { ModularUIModel } from "../models/types";

/**
 * Helper for fetching data and contributions from the Be Informed modular ui
 * and merge it into a target or resolvable model.
 */
class ModularUIRequest {
  _response: ModularUIResponse;

  _href: Href;
  _options: RequestModularUIOptions;
  _targetModel: ?TargetModel;
  _forceTargetModel: boolean = false;
  _contributionsHref: string;
  _locale: string;
  _method: $Keys<typeof HTTP_METHODS> = HTTP_METHODS.GET;

  _progressEvent: ProgressEventHandler;

  _origin: string = "";
  _contextPath: string = getBasePathModularUI();

  /**
   */
  constructor(
    href: Href | string,
    options: $Shape<RequestModularUIOptions> = {},
  ) {
    if (!href) {
      throw new IllegalArgumentException(
        "You must pass a href to the resource that is requested",
      );
    }

    this.options = options;

    this._response = new ModularUIResponse();

    this.href = typeof href === "string" ? new Href(href) : href;
    this.locale = this.getRequestLocale(options.locale);

    // copy request parameters to response, to be able to use them in the models
    // self links are missing the request parameters
    this._response.parameters = this.href.parameters;

    if (options.contextPath) {
      this.contextPath = options.contextPath;
    } else if (this.href.contextPath) {
      this.contextPath = this.href.contextPath;
    }

    if (options.origin) {
      this.origin = options.origin;
    } else if (this.href.origin) {
      this.origin = this.href.origin;
    }

    if (options.targetModel) {
      this.targetModel = options.targetModel;
    }

    if (options.method) {
      this.method = options.method;
    } else {
      this.method = this.href.method;
    }
  }

  /**
   * Retrieve the preferred language for this request, falls back to the browser language or english
   */
  getRequestLocale(optionLocale: ?string): string {
    if (optionLocale) {
      return optionLocale;
    }

    // $FlowIssue
    return globalThis.navigator?.language?.split("-")[0] || "en";
  }

  /**
   */
  get origin(): string {
    return this._origin;
  }

  /**
   */
  set origin(origin: ?string) {
    this._origin = origin ?? "";
    this._response.origin = origin ?? "";
  }

  /**
   */
  get contextPath(): string {
    return this._contextPath;
  }

  /**
   */
  set contextPath(contextPath: ?string) {
    this._contextPath = contextPath ?? getBasePathModularUI();
    this._response.contextPath = contextPath ?? getBasePathModularUI();
  }

  /**
   */
  get contributionsHref(): string {
    return this._contributionsHref;
  }

  /**
   */
  set contributionsHref(contributionsHref: string) {
    this._contributionsHref = contributionsHref;
  }

  /**
   */
  set locale(locale: string) {
    this._locale = locale;
    this._response.locale = locale;
  }

  /**
   */
  get locale(): string {
    return this._locale;
  }

  /**
   */
  get response(): ModularUIResponse {
    return this._response;
  }

  /**
   */
  set href(href: Href) {
    this._href = href;
  }

  /**
   */
  get href(): Href {
    return this._href;
  }

  /**
   */
  set method(method: $Keys<typeof HTTP_METHODS>) {
    this._method = method;
  }

  /**
   */
  get method(): $Keys<typeof HTTP_METHODS> {
    return this._method;
  }

  /**
   */
  get options(): RequestModularUIOptions {
    return {
      ...this._options,
      locale: this.locale,
      method: this.method,
    };
  }

  /**
   */
  get requestOptions(): RequestBaseOptions {
    /* eslint-disable no-unused-vars */
    const {
      targetModel,
      forceTargetModel,
      updateHandler,
      childmodels,
      isValidationRequest,
      removeOnUnmount,
      ...requestOptions
    } = this.options;
    /* eslint-enable no-unused-vars */
    return requestOptions;
  }

  /**
   */
  set options(options: RequestModularUIOptions) {
    this._options = options;
  }

  /**
   */
  get withChildModels(): boolean {
    return (
      !("childmodels" in this.options) || this.options.childmodels === true
    );
  }

  /**
   */
  set targetModel(targetModel: TargetModel) {
    this._targetModel = targetModel;
  }

  /**
   */
  get targetModel(): ?TargetModel {
    return this._targetModel;
  }

  /**
   */
  set forceTargetModel(forceTargetModel: boolean) {
    this._forceTargetModel = forceTargetModel ?? false;
  }

  /**
   */
  get forceTargetModel(): boolean {
    return this._forceTargetModel;
  }

  /**
   */
  resolveModel(): Class<ModularUIModel> {
    let availableModels;
    if (this.targetModel) {
      availableModels = Array.isArray(this.targetModel)
        ? [...this.targetModel]
        : [this.targetModel];

      if (this.forceTargetModel) {
        return availableModels[0];
      }
    }

    const Model: Class<ModularUIModel> | null = resolveModel(
      this.response,
      availableModels,
    );

    if (Model?.isApplicableModel) {
      return Model;
    }

    if (availableModels) {
      throw new IllegalStateException(
        `data for ${this.href.toString()} is not applicable for model(s): ${availableModels
          .map((m) => m.modelName)
          .join(", ")}, received response: ${JSON.stringify(this.response)}`,
      );
    }

    throw new IllegalStateException(
      `no javascript model is applicable for received request of ${this.href.toString()}, with response: ${JSON.stringify(
        this.response,
      )}`,
    );
  }

  /**
   */
  createModel(): ModularUIModel {
    const Model = this.resolveModel();
    return new Model(this.response);
  }

  /**
   */
  processContributionsService(contributionsData: Object): void {
    if (!isPlainObject(contributionsData)) {
      throw new IllegalArgumentException(
        "Missing contributions data or contributions is not JSON",
      );
    }

    const [contributionsKey] = Object.keys(contributionsData);

    if (contributionsData.error && contributionsKey === "error") {
      this.handleContributionsError(contributionsData.error);
    }

    // The key of the data service is different from the contributions service for forms
    if (!(this.response.key in contributionsData)) {
      this.response.key = contributionsKey;
    }

    this.response.contributions = contributionsData[this.response.key];
  }

  /**
   */
  handleContributionsError(error: Object): void {
    const errorMessage = error.properties?.message ?? "Error in contribution";
    throw new ModularUIError(errorMessage, error, this.contributionsHref);
  }

  /**
   */
  handleDataError(error: Object): void {
    if (error.properties) {
      const errorMessage = error.properties?.message ?? "Error in data";
      throw new ModularUIError(
        errorMessage,
        error,
        this.href.absolutepath.toString(),
      );
    }

    throw new IllegalArgumentException(error);
  }

  /**
   */
  handleData(key: string, data: Object): void {
    this.response.key = key;
    this.response.data = data;

    const links = data._links;

    if (links?.contributions) {
      this.contributionsHref = links.contributions.href;
    } else if (Array.isArray(links) && links[0].contributions) {
      this.contributionsHref = links[0].contributions.href;
    } else {
      throw new IllegalArgumentException(
        `Contributions link not found for data with key ${key}`,
      );
    }
  }

  /**
   */
  processDataService(data: Object): void {
    if (!isPlainObject(data)) {
      throw new IllegalArgumentException("Missing data or data is not JSON");
    }

    if (data.error) {
      this.handleDataError(data.error);
    } else {
      const [key] = Object.keys(data);
      this.handleData(key, data[key]);
    }
  }

  /**
   */
  fetchContributionsService(): Promise<any> {
    if (!this.contributionsHref) {
      throw new IllegalStateException("Missing a contributions href");
    }

    return universalFetch({
      url: `${this.origin}${this.contextPath}${this.contributionsHref}`,
      cache: true,
      locale: this.options.locale,
    });
  }

  /**
   */
  fetchDataService(): Promise<any> {
    return universalFetch({
      ...this.requestOptions,
      url: `${this.origin}${this.contextPath}${this.href.path}`,
      params: this.href.getQuerystringForModularUI(),
      locale: this.options.locale,
      onProgress: this.onProgress,
    });
  }

  /**
   * Check if links contain a 'hide-when-empty' layout hint and remove the link from
   * the component when it exists and no results are available
   */
  async processAsyncLayoutHints(): Promise<void> {
    if (getSetting("ALLOW_HIDE_WHEN_EMPTY_ON_TABS")) {
      await this.processApplicationLinks();
      await this.processTabLinks();
    }
    await this.processPanelLinks();
  }

  /**
   * hide application links<br>
   * _links on application data contains an extra level of tab links
   */
  processApplicationLinks(): Promise<void> {
    const contributionLinks = this.response.contributions._links;
    const dataLinks = this.response.data._links;
    if (!contributionLinks || !dataLinks) {
      return Promise.resolve();
    }

    if ("tab" in contributionLinks && "tab" in dataLinks) {
      for (const contributionTabLink of contributionLinks.tab) {
        if ("components" in contributionTabLink) {
          const dataTabLinks = dataLinks.tab.find(
            (dataLink) => dataLink.name === contributionTabLink.name,
          );

          if (dataTabLinks && "components" in dataTabLinks) {
            return this.processLinks(
              dataTabLinks.components,
              contributionTabLink.components,
            ).then((newLinks) =>
              this.replaceApplicationLinks(contributionTabLink.name, newLinks),
            );
          }
        }
      }
    }

    return Promise.resolve();
  }

  /**
   * Replace hidden link in application link tree
   */
  replaceApplicationLinks(tabName: string, newLinks: Array<Object>): void {
    this.response.data._links.tab = this.response.data._links.tab.map(
      (tabLink) => {
        const key = tabLink.name;
        if (tabName === key) {
          tabLink.components = newLinks;
        }
        return tabLink;
      },
    );
  }

  /**
   */
  processTabLinks(): Promise<void> {
    const contributionLinks = this.response.contributions._links;

    if (!contributionLinks) {
      return Promise.resolve();
    }

    if ("component" in contributionLinks) {
      return this.processLinks(
        this.response.data._links.component,
        contributionLinks.component,
      ).then((newLinks) => {
        this.response.data._links.component = newLinks;
      });
    }

    return Promise.resolve();
  }

  /**
   */
  processPanelLinks(): Promise<void> {
    const contributionLinks = this.response.contributions._links;

    if (!contributionLinks) {
      return Promise.resolve();
    }

    if ("panel" in contributionLinks) {
      return this.processLinks(
        this.response.data._links,
        contributionLinks.panel,
      ).then((newLinks) => {
        this.response.data._links = newLinks;
      });
    }

    return Promise.resolve();
  }

  /**
   * Retrieve an object of key: link
   */
  dataLinksPerName(dataLinks: any): { [linkName: string]: any } {
    if (Array.isArray(dataLinks)) {
      const links = {};
      for (const dataLink of dataLinks) {
        links[dataLink.name] = dataLink;
      }
      return links;
    }
    return dataLinks;
  }

  /**
   * Check if links are empty and hide them when hide-when-empty hint exists
   */
  processLinks(
    dataLinks: any,
    contributionLinks: any,
  ): Promise<Object | Array<Object>> {
    const linksPerName = this.dataLinksPerName(dataLinks);
    const hideWhenEmptyLinks = contributionLinks.filter((contributionLink) =>
      contributionLink.layouthint?.includes(HIDE_WHEN_EMPTY),
    );

    const isHiddenListChecks = [];

    for (const contributionLink of hideWhenEmptyLinks) {
      const dataLink = linksPerName[contributionLink.name];
      if (dataLink?.href) {
        isHiddenListChecks.push(
          this.isHiddenList(contributionLink.name, dataLink.href),
        );
      }
    }

    return Promise.all(isHiddenListChecks).then(
      (hiddenLinks: Array<string>) => {
        if (Array.isArray(dataLinks)) {
          return dataLinks.filter(
            (dataLink) => !hiddenLinks.includes(dataLink.name),
          );
        }

        const newDataLinks: { [string]: any } = {};
        for (const dataLinkKey in dataLinks) {
          if (!hiddenLinks.includes(dataLinkKey)) {
            newDataLinks[dataLinkKey] = dataLinks[dataLinkKey];
          }
        }
        return newDataLinks;
      },
    );
  }

  /**
   */
  isHiddenList(name: string, href: string): Promise<string> {
    const HIDE_WHEN_EMPTY_IGNORE_TASKS = getSetting(
      "HIDE_WHEN_EMPTY_IGNORE_TASKS",
      false,
    );

    return universalFetch({
      ...this.requestOptions,
      url: `${this.origin}${this.contextPath}${href}`,
    })
      .then((response: Object) => {
        if (isPlainObject(response)) {
          const [key] = Object.keys(response);
          const { _embedded, actions } = response[key];

          const noListItems = _embedded === null;
          const noActions = actions == null || HIDE_WHEN_EMPTY_IGNORE_TASKS;

          const isHidden = noListItems && noActions;
          if (isHidden) {
            return name;
          }
        }
        return "";
      })
      .catch(() => {
        return "";
      });
  }

  /**
   */
  set onProgress(progressEvent: ProgressEventHandler) {
    this._progressEvent = progressEvent;
  }

  /**
   */
  get onProgress(): ProgressEventHandler {
    return this._progressEvent || null;
  }

  /**
   * Combine previous send request data with new validation data to create a complete request object
   * The received model is new FormModel containing ONLY the current question, not the previously entered questions
   * to create a complete request we append the originally send form objects
   */
  getDynamicValidationData(model: ModularUIModel): string {
    const prevData =
      typeof this.options.data === "string"
        ? JSON.parse(this.options.data)
        : this.options.data;

    if (model instanceof FormModel) {
      const newData = JSON.parse(model.validationData);
      // $FlowFixMe incompatible-call
      return JSON.stringify(deepmerge(prevData || {}, newData));
    }

    return JSON.stringify(prevData) ?? "{}";
  }

  /**
   * First load of dynamic values when a form is loaded
   */
  loadDynamicValidations(model: ModularUIModel): Promise<ModularUIModel> {
    if (
      !this.options.isValidationRequest &&
      getSetting("USE_INSTANT_SERVER_VALIDATION") &&
      model instanceof FormModel &&
      model.currentFormObject?.hasDynamicValidations
    ) {
      const validationHref = this.href.setParameter("commit", "false");
      return universalFetch({
        ...this.requestOptions,
        url: `${this.origin}${this.contextPath}${this.href.path}`,
        params: validationHref.getQuerystringForModularUI(),
        data: this.getDynamicValidationData(model),
      }).then((data) => {
        if (data == null || typeof data !== "object" || Array.isArray(data)) {
          return model;
        }

        const [key] = Object.keys(data);
        return model.updateValidations(data[key]);
      });
    }

    return Promise.resolve(model);
  }

  /**
   */
  fetch(): Promise<ModularUIModel> {
    return this.fetchDataService()
      .then((data) => {
        if (data == null) {
          throw new Error(`No data received for ${this.href.toString()}`);
        }

        this.processDataService(data);
        return this.fetchContributionsService();
      })
      .then((contributionsData) => {
        if (contributionsData == null) {
          throw new Error(
            `No contributions data received for ${this.contributionsHref}`,
          );
        }

        this.processContributionsService(contributionsData);
        return this.processAsyncLayoutHints();
      })
      .then(() => this.createModel())
      .then((model) => this.loadDynamicValidations(model))
      .then((model) => {
        if (this.withChildModels) {
          return this.fetchChildModels(model);
        }

        return model;
      });
  }

  /**
   */
  fetchFromCache(): Promise<ModularUIModel> {
    this.options = {
      ...this.options,
      cache: true,
    };

    return this.fetch();
  }

  /**
   */
  fetchChildModels(model: ModularUIModel): Promise<ModularUIModel> {
    const childModelLinks = model.getInitialChildModelLinks();

    const childModelRequests = childModelLinks.map((childModelLink) => {
      const request = new ModularUIRequest(childModelLink.href, {
        locale: this.locale,
      });

      if (childModelLink.targetModel) {
        request.targetModel = childModelLink.targetModel;
      }

      if (childModelLink.isCacheable) {
        return request.fetchFromCache();
      }

      return request.fetch();
    });

    return Promise.allSettled(childModelRequests).then((childModels) => {
      const resolvedChildModels = [];
      const errorChildModels = [];

      childModels.forEach((childModel) => {
        if (childModel.status === "rejected") {
          const { reason } = childModel;
          if (reason.name !== "Error.ChangePasswordRequired") {
            // eslint-disable-next-line no-console
            console.debug(
              "Unexpected error when retrieving child model:",
              reason,
            );
          }
          errorChildModels.push(
            new ErrorResponse(reason, "", {
              origin: this.origin,
              contextPath: this.contextPath,
            }),
          );
        } else {
          resolvedChildModels.push(childModel.value);
        }
      });

      model.addChildModels(resolvedChildModels, errorChildModels);

      return model;
    });
  }

  /**
   */
  fetchContent(withChildSections: boolean): Promise<mixed> {
    return this.fetchFromCache().then((model) => {
      if (
        withChildSections &&
        model instanceof ContentModel &&
        model.childSectionLinks.length > 0
      ) {
        return this.fetchContentChildSections(model);
      }

      return model;
    });
  }

  /**
   * Recursively return child sections defined on the content model
   */
  fetchContentChildSections(contentModel: ContentModel): Promise<mixed> {
    const newContentModel = contentModel.clone();

    return Promise.all(
      contentModel.childSectionLinks.map((childSectionLink) => {
        const contentHrefWithEntryDate = childSectionLink.selfhref.addParameter(
          TIMEVERSION_FILTER_NAME,
          contentModel.entryDate,
        );

        const request = new ModularUIRequest(contentHrefWithEntryDate, {
          locale: this.locale,
        });

        return request.fetchContent(true);
      }),
    ).then((sectionModels) => {
      newContentModel.childSections = sectionModels;

      return newContentModel;
    });
  }

  /**
   * Make a post request with optionally data to send
   * @param data
   */
  post(data?: string | { [key: string]: string }): Promise<ModularUIModel> {
    this.method = HTTP_METHODS.POST;
    this.options = {
      ...this.options,
      data,
    };

    return this.fetch();
  }

  /**
   * Make a POST request using the form, an updated form is returned (merge of old and new form)
   * @param form
   * @returns {Promise<FormModel>}
   */
  postForm(form: FormModel): Promise<FormModel> {
    this.method = HTTP_METHODS.POST;
    this.options = {
      ...this.options,
      data: form.formdata,
    };

    return this.fetch().then((response) => {
      return form.update(response);
    });
  }

  /**
   * Simplified synchronous version of fetch (returns the model, not a Promise)
   * This can be used on the server to retrieve a model. It only loads the request url and it's child models
   */
  fetchSync(): ModularUIModel {
    const options = { headers: { "Accept-Language": this.locale } };

    const dataString = dataFetcher.fetch(this.href.toString(), options);
    if (dataString == null) {
      throw new Error(`No data received for ${this.href.toString()}`);
    }

    this.processDataService(JSON.parse(dataString));

    const contributionsString = dataFetcher.fetch(
      this.contributionsHref.toString(),
      options,
    );
    if (contributionsString == null) {
      throw new Error(
        `No constributions received for ${this.contributionsHref.toString()}`,
      );
    }
    this.processContributionsService(JSON.parse(contributionsString));

    const model = this.createModel();

    const childModelLinks = model.getInitialChildModelLinks();

    const childModels = childModelLinks.map((childModelLink) => {
      return new ModularUIRequest(childModelLink.href, {
        locale: this.locale,
      }).fetchSync();
    });
    model.addChildModels(childModels, []);

    return model;
  }
}

export default ModularUIRequest;
